import { DateTime } from 'luxon';
import { type TimeRange } from '@centric-os/types';
import { isNil } from 'lodash-es';

export default () => {
  /**
   * Converts a timestamp to a Date for the date/time pickers: the Date's local wall clock
   * equals the timestamp's wall clock in the given timezone.
   * @param timestamp The timestamp to convert to date
   * @param timezone The timezone to read the timestamp in. If not provided, UTC is used
   * @returns a Date whose local wall clock matches the timestamp in the zone
   */
  const getDateFromTimestamp = (timestamp: number, timezone: string = 'UTC'): Date =>
    DateTime.fromMillis(timestamp, { zone: timezone })
      .setZone('local', { keepLocalTime: true })
      .toJSDate();

  /**
   * Converts a time string (e.g., "08:00") into a Luxon DateTime object.
   *
   * @param {string} timeString - The time string in "HH:mm" format.
   * @returns {DateTime} - A Luxon DateTime object representing the time.
   */
  const getDateTimeFromTimeString = (timeString: string): DateTime => {
    if (!timeString) {
      throw new Error(`Invalid time string: "${timeString}"`);
    }
    return DateTime.fromFormat(timeString, 'HH:mm');
  };

  /**
   * Converts a DateTime object to the total number of minutes since midnight.
   * @param {DateTime} date - The Luxon DateTime object.
   * @returns {number} - The total number of minutes since midnight.
   */
  const toMinutes = (date: DateTime): number => date.hour * 60 + date.minute;

  /**
   * Gets the next day of the week.
   * @param {number} day - The current day of the week (0-6, where 0 is Sunday).
   * @returns {number} - The next day of the week (0-6).
   */
  const getNextDay = (day: number): number => (day === 6 ? 0 : day + 1);

  /**
   * Checks if a time range is overnight.
   * @param {TimeRange} range - The time range with start and end times.
   * @returns {boolean} - True if the range is overnight, false otherwise.
   */
  const isOvernight = (range: TimeRange): boolean => range.end <= range.start;

  /**
   * Adds the next day to a list of weekdays if the time range is overnight.
   * @param {number[]} weekdays - The list of weekdays (0-6).
   * @param {TimeRange} range - The time range with start and end times.
   * @returns {number[]} - The updated list of weekdays including the next day if overnight.
   */
  const addNextDay = (weekdays: number[], range: TimeRange): number[] => {
    if (!isOvernight(range)) return weekdays;
    const nextDays = weekdays.map(getNextDay);
    return Array.from(new Set([...weekdays, ...nextDays])).toSorted((a, b) => a - b);
  };

  /**
   * Formats a date string based on whether it's today, yesterday, or another date
   * @param {string} isoDate ISO format date string
   * @returns {string} Formatted date string
   */
  const formatRelativeDate = (isoDate: string): string => {
    if (!isoDate) {
      return 'Invalid DateTime';
    }

    const orderDate: DateTime = DateTime.fromISO(isoDate);
    if (!orderDate.isValid) {
      return 'Invalid DateTime';
    }

    // Check if the date is today
    const todaysDate: DateTime = DateTime.now();
    if (orderDate.hasSame(todaysDate, 'day')) {
      return `Today at ${orderDate.toFormat('h:mm a').replace(/(AM|PM)$/i, (match) => match.toLowerCase())}`;
    }

    // Check if the date is yesterday
    const yesterdayDate: DateTime = todaysDate.minus({ days: 1 });
    if (orderDate.hasSame(yesterdayDate, 'day')) {
      return `Yesterday at ${orderDate.toFormat('h:mm a').replace(/(AM|PM)$/i, (match) => match.toLowerCase())}`;
    }

    // All other dates
    return orderDate
      .toFormat("MMMM dd 'at' h:mm a")
      .replace(/(AM|PM)$/i, (match) => match.toLowerCase());
  };

  /**
   * Updates a given date to represent the start of the day (00:00:00.000).
   *
   * @param {Date} date - The date to update to the start of the day.
   * @returns {Date|null} A new `Date` object with the start of the day applied, or `null` if the input is `null` or `undefined`.
   */
  const setStartOfDay = (date: Date): Date => {
    if (isNil(date)) {
      return null;
    }
    return DateTime.fromJSDate(date).startOf('day').toJSDate();
  };

  /**
   * Updates a given date to represent the end of the day (23:59:59.999).
   *
   * @param {Date} date - The date to update to the end of the day.
   * @returns {Date|null} A new `Date` object with the end of the day applied, or `null` if the input is `null` or `undefined`.
   */
  const setEndOfDay = (date: Date): Date => {
    if (isNil(date)) {
      return null;
    }
    return DateTime.fromJSDate(date).endOf('day').toJSDate();
  };

  return {
    getDateFromTimestamp,
    getDateTimeFromTimeString,
    toMinutes,
    getNextDay,
    isOvernight,
    addNextDay,
    formatRelativeDate,
    setStartOfDay,
    setEndOfDay,
  };
};
